import * as THREE from 'three';
import type { V2 } from '../core/math';

/**
 * Stingers (spike strips) laid across the road at a level-4 roadblock (docs/POLICE-AI-POLISH.md): a dark strip
 * with yellow chevrons, readable from the top-down camera at 30 m. One mesh pair per strip, no per-frame work.
 */
export class StingerView {
  private readonly group = new THREE.Group();
  private readonly strips = new Map<number, THREE.Group>();
  private readonly dark = new THREE.MeshLambertMaterial({ color: 0x24221f });
  private readonly yellow = new THREE.MeshLambertMaterial({ color: 0xe0b020 });

  constructor(scene: THREE.Scene) {
    this.group.name = 'stingers';
    scene.add(this.group);
  }

  add(id: number, a: V2, b: V2): void {
    if (this.strips.has(id)) return;
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    const g = new THREE.Group();
    const base = new THREE.Mesh(new THREE.BoxGeometry(len, 0.08, 0.5), this.dark);
    base.position.y = 0.04;
    g.add(base);
    // Chevrons: short yellow blocks every metre, slightly proud of the strip.
    const n = Math.max(2, Math.floor(len / 1.2));
    const chev = new THREE.BoxGeometry(0.4, 0.05, 0.54);
    for (let i = 0; i < n; i++) {
      const m = new THREE.Mesh(chev, this.yellow);
      m.position.set(-len / 2 + (i + 0.5) * (len / n), 0.085, 0);
      g.add(m);
    }
    g.position.set((a.x + b.x) / 2, 0, (a.z + b.z) / 2);
    g.rotation.y = -Math.atan2(b.z - a.z, b.x - a.x);
    this.group.add(g);
    this.strips.set(id, g);
  }

  remove(id: number): void {
    const g = this.strips.get(id);
    if (!g) return;
    this.group.remove(g);
    for (const c of g.children) (c as THREE.Mesh).geometry.dispose();
    this.strips.delete(id);
  }

  dispose(): void {
    for (const id of [...this.strips.keys()]) this.remove(id);
    this.group.parent?.remove(this.group);
    this.dark.dispose();
    this.yellow.dispose();
  }
}
